import { LabelWithHighlights } from '@euler/app/components/LabelWithHighlights';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FuseResultMatch } from 'fuse.js';
import { memo, useCallback } from 'react';
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';
import { useTheme } from 'styled-components';
import { kSubjectTypeInfoMap } from '../functions/subjectTypeInfoUtil';
import { ServiceSubjectItem } from '../types';

export const ServiceSubjectCell = memo(
  ({
    item,
    matches,
    selected,
    onPress,
    readonly,
    first,
    last,
    style,
    border,
  }: {
    first?: boolean;
    last?: boolean;
    item: ServiceSubjectItem;
    matches?: ReadonlyArray<FuseResultMatch>;
    selected: boolean;
    readonly?: boolean;
    border?: boolean;
    onPress: (item: ServiceSubjectItem) => void;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const subjectTypeInfo = kSubjectTypeInfoMap[item.type];
    const {
      construction: {
        schedule: {
          workItem: {
            cell: { textLight, bgLight },
          },
        },
      },
    } = useTheme();

    const onCellPress = useCallback(() => {
      onPress?.(item);
    }, [item, onPress]);

    return (
      <TouchableOpacity
        onPress={onCellPress}
        css={`
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
          min-height: ${rpx(44)}px;
          border-radius: ${rpx(22)}px;
          padding-left: ${rpx(15)}px;
          background-color: ${bgLight};
          margin: ${rpx(5)}px ${rpx(10)}px;
          margin-top: ${first ? 0 : rpx(5)}px;
          margin-bottom: ${rpx(last ? 15 : 5)}px;
          padding-top: ${rpx(8)}px;
          padding-bottom: ${rpx(8)}px;
        `}
        style={style}
      >
        {border ? (
          <View
            css={`
              position: absolute;
              left: ${rpx(-10)}px;
              right: ${rpx(-10)}px;
              bottom: 0;
              height: 1px;
              background-color: #ddd;
            `}
          />
        ) : null}
        {item.iconUrl ? (
          <Img
            uri={item.iconUrl}
            css={`
              width: ${rpx(32)}px;
              height: ${rpx(32)}px;
              border-radius: ${rpx(8)}px;
              margin-right: ${rpx(10)}px;
              flex-shrink: 0;
            `}
          />
        ) : null}
        <View
          css={`
            flex: 1;
          `}
        >
          <HStack justifyContent="flex-start" alignItems="center">
            <LabelWithHighlights
              regular
              title={item.title}
              matches={matches}
              size={rpx(item.description ? 16 : 15)}
              numberOfLines={0}
              color={textLight}
              lineHeight={rpx(20)}
              css={`
                flex: 1;
                margin-right: ${rpx(8)}px;
              `}
            />
            {item.showTypeLabel && (
              <Center
                css={`
                  border-radius: ${rpx(3)}px;
                  height: ${rpx(20)}px;
                  padding: 0 ${rpx(5)}px;
                  background-color: ${subjectTypeInfo.bgColor};
                  margin-left: ${rpx(5)}px;
                `}
              >
                <Typography.Label
                  color={subjectTypeInfo.textColor}
                  light
                  size={rpx(10)}
                  lineHeight={rpx(20)}
                >
                  {subjectTypeInfo.text}
                </Typography.Label>
              </Center>
            )}
          </HStack>
          {item.description ? (
            <Typography.Label
              regular
              size={rpx(12)}
              numberOfLines={1}
              family="light"
              color="#777"
              css={`
                margin-top: ${rpx(4)}px;
              `}
            >
              {item.description}
            </Typography.Label>
          ) : null}
        </View>
        <TouchableOpacity
          onPress={onCellPress}
          css={`
            width: ${rpx(50)}px;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          `}
          hitSlop={PredefinedHitSlops.large}
        >
          <Icon
            name={selected ? 'minus-circle' : 'plus-circle'}
            color={
              readonly
                ? '#ccc'
                : selected
                ? StatusColors.Danger
                : StatusColors.Info
            }
            size={rpx(24)}
          />
        </TouchableOpacity>
      </TouchableOpacity>
    );
  },
);
